// SCR-STAFF-108 — افزوده‌های فاز 1 تنظیمات مراجعه‌کننده (AP-PAT-02): استعلام خودکار شاهکار و روزهای «دیرکرد» پرونده موقت
// (US-PAT-020، US-PAT-007)، برچسب‌های مدیریت‌شده (US-PAT-060؛ نام بالینی رد — PHX-PAT-0801) و قالب‌های نسخه‌دار رضایت (US-PAT-050؛
// متن نمونه با نشان «نمونه»؛ هر انتشار نسخه تازه است و نسخه قبلی تغییر نمی‌کند).
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type ConsentTemplateRequest,
  type ConsentTemplateView,
  type ConsentType,
  type TagDefinitionRequest,
  type TagDefinitionView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, SampleBadge, toJalaliDateTime, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { patKeys, useTagDefinitions } from './common';

const CONSENT_TYPES: ConsentType[] = [
  'DataProcessing',
  'TransactionalMessaging',
  'MarketingMessaging',
  'ImageUseForMarketing',
  'ImageUseForEducation',
  'PortalSharingWithCompanion',
];

export function PreferencesSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: patKeys.preferencesSettings(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/settings/preferences')),
  });
  const [auto, setAuto] = useState(false);
  const [days, setDays] = useState('7');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (settings.data) {
      setAuto(settings.data.autoIdentityInquiry);
      setDays(String(settings.data.incompleteAfterDays));
    }
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/patients/settings/preferences', {
          body: { autoIdentityInquiry: auto, incompleteAfterDays: Number(days) },
        }),
      ),
    onMutate: () => setSaved(false),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.preferencesSettings(session?.tenantId), data);
      setSaved(true);
    },
  });
  const daysInvalid = !/^\d{1,2}$/.test(days) || Number(days) < 1 || Number(days) > 90;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="patient-preferences-settings">
      <Typography variant="h2" gutterBottom>
        {t('pat:settings.preferences.title')}
      </Typography>
      {settings.error ? <ErrorState error={settings.error} compact onRetry={() => void settings.refetch()} /> : null}
      <Stack spacing={2}>
        {saved && <Alert severity="success">{t('pat:settings.preferences.saved')}</Alert>}
        <FormControlLabel
          control={<Switch checked={auto} onChange={(event) => setAuto(event.target.checked)} disabled={!canWrite} data-testid="auto-inquiry" />}
          label={t('pat:settings.preferences.auto_inquiry')}
        />
        <Typography variant="body2" color="text.secondary">
          {t('pat:settings.preferences.auto_inquiry_help')}
        </Typography>
        <TextField
          label={t('pat:settings.preferences.incomplete_days')}
          value={days}
          onChange={(event) => setDays(event.target.value)}
          disabled={!canWrite}
          error={daysInvalid}
          helperText={daysInvalid ? t('errors:validation.range') : undefined}
          sx={{ maxWidth: 240 }}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
        />
        {save.error ? <ErrorState error={save.error} compact /> : null}
        {canWrite && (
          <div>
            <Button variant="contained" onClick={() => save.mutate()} disabled={save.isPending || daysInvalid} data-testid="preferences-settings-save">
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

function TagDefinitionDialog({ definition, open, onClose }: { definition: TagDefinitionView | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const creating = definition === null;
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState('#1976d2');
  const [active, setActive] = useState(true);
  useEffect(() => {
    if (open) {
      setCode(definition?.code ?? '');
      setName(definition?.name ?? '');
      setColor(definition?.color ?? '#1976d2');
      setActive(definition?.isActive ?? true);
    }
  }, [open, definition]);
  const save = useMutation({
    mutationFn: (body: TagDefinitionRequest) =>
      creating
        ? unwrap(tenantApi.POST('/api/v1/patients/tag-definitions', { body, headers: { 'Idempotency-Key': idempotency.current() } }))
        : unwrap(tenantApi.PUT('/api/v1/patients/tag-definitions/{id}', { params: { path: { id: definition.id } }, body })),
    onSettled: (_data, error) => {
      if (creating) {
        idempotency.settle(error ?? undefined);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...patKeys.all(session?.tenantId), 'tag-definitions'] });
      onClose();
    },
  });
  const invalid = !name.trim() || (creating && !/^[a-z][a-z0-9_]{1,39}$/.test(code));
  return (
    <FormDialog
      open={open}
      titleKey={creating ? 'pat:settings.tags.add' : 'pat:settings.tags.edit'}
      screenId="SCR-STAFF-108-TAG"
      onSubmit={() => !invalid && save.mutate({ code, name: name.trim(), color, isActive: active })}
      onClose={onClose}
      isDirty={name.length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <TextField
        label={t('pat:settings.tags.code')}
        value={code}
        onChange={(event) => setCode(event.target.value)}
        disabled={!creating}
        required
        helperText={t('pat:settings.tags.code_help')}
        slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'tag-code' } }}
      />
      <TextField
        label={t('pat:settings.tags.name')}
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        slotProps={{ htmlInput: { maxLength: 30, 'data-testid': 'tag-name' } }}
      />
      <TextField
        type="color"
        label={t('pat:settings.tags.color')}
        value={color}
        onChange={(event) => setColor(event.target.value)}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      {!creating && (
        <FormControlLabel control={<Checkbox checked={active} onChange={(event) => setActive(event.target.checked)} />} label={t('pat:settings.tags.active')} />
      )}
    </FormDialog>
  );
}

export function TagDefinitionsSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const definitions = useTagDefinitions(true);
  const [editing, setEditing] = useState<TagDefinitionView | null | undefined>(undefined);
  const columns: DataTableColumn<TagDefinitionView>[] = [
    {
      id: 'name',
      headerKey: 'pat:settings.tags.name',
      cell: (row) => <Chip size="small" variant="outlined" sx={{ borderColor: row.color }} label={row.name} />,
    },
    { id: 'code', headerKey: 'pat:settings.tags.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    { id: 'active', headerKey: 'pat:settings.tags.active', cell: (row) => t(row.isActive ? 'pat:settings.tags.yes' : 'pat:settings.tags.no') },
    {
      id: 'actions',
      headerKey: 'pat:settings.tags.edit',
      cell: (row) =>
        canWrite ? (
          <IconButton aria-label={t('pat:settings.tags.edit')} onClick={() => setEditing(row)}>
            <EditOutlinedIcon />
          </IconButton>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="tag-definitions">
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:settings.tags.title')}
        </Typography>
        {canWrite && (
          <Button startIcon={<AddIcon />} onClick={() => setEditing(null)} data-testid="tag-definition-add">
            {t('pat:settings.tags.add')}
          </Button>
        )}
      </Stack>
      <DataTable
        captionKey="pat:settings.tags.title"
        columns={columns}
        rows={definitions.data}
        getRowId={(row) => row.id}
        isLoading={definitions.isPending}
        error={definitions.error}
        emptyTitleKey="pat:settings.tags.empty"
      />
      <TagDefinitionDialog definition={editing ?? null} open={editing !== undefined} onClose={() => setEditing(undefined)} />
    </Paper>
  );
}

function ConsentTemplateDialog({ type, open, onClose }: { type: ConsentType | null; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [selected, setSelected] = useState<ConsentType>('DataProcessing');
  const [body, setBody] = useState('');
  const [reconsent, setReconsent] = useState(false);
  useEffect(() => {
    if (open) {
      setSelected(type ?? 'DataProcessing');
      setBody('');
      setReconsent(false);
    }
  }, [open, type]);
  const save = useMutation({
    mutationFn: (request: ConsentTemplateRequest) =>
      unwrap(tenantApi.POST('/api/v1/patients/consent-templates', { body: request, headers: { 'Idempotency-Key': idempotency.current() } })),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: patKeys.consentTemplates(session?.tenantId) });
      onClose();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="pat:settings.consents.publish"
      screenId="SCR-STAFF-108-CONSENT"
      onSubmit={() => body.trim() && save.mutate({ type: selected, body: body.trim(), requiresReconsent: reconsent })}
      onClose={onClose}
      isDirty={body.length > 0}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <TextField select label={t('pat:consents.type_label')} value={selected} onChange={(event) => setSelected(event.target.value as ConsentType)}>
        {CONSENT_TYPES.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`pat:consents.types.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label={t('pat:settings.consents.body')}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        multiline
        minRows={6}
        required
        slotProps={{ htmlInput: { 'data-testid': 'consent-template-body' } }}
      />
      <FormControlLabel
        control={<Checkbox checked={reconsent} onChange={(event) => setReconsent(event.target.checked)} />}
        label={t('pat:settings.consents.requires_reconsent')}
      />
    </FormDialog>
  );
}

export function ConsentTemplatesSection({ canWrite }: { canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const templates = useQuery({
    queryKey: patKeys.consentTemplates(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/consent-templates')),
  });
  const [publishing, setPublishing] = useState<ConsentType | null | undefined>(undefined);
  const latest = CONSENT_TYPES.map((type) =>
    (templates.data?.items ?? []).filter((item) => item.type === type).sort((a, b) => Number(b.version) - Number(a.version))[0],
  ).filter((item): item is ConsentTemplateView => Boolean(item));
  const columns: DataTableColumn<ConsentTemplateView>[] = [
    {
      id: 'type',
      headerKey: 'pat:consents.type_label',
      cell: (row) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>{t(`pat:consents.types.${row.type}`)}</span>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    { id: 'version', headerKey: 'pat:consents.version_label', cell: (row) => faDigits(row.version) },
    { id: 'at', headerKey: 'pat:settings.consents.published_at', cell: (row) => toJalaliDateTime(row.createdAt) },
    {
      id: 'actions',
      headerKey: 'pat:settings.consents.publish',
      cell: (row) =>
        canWrite ? (
          <Button size="small" onClick={() => setPublishing(row.type)}>
            {t('pat:settings.consents.new_version')}
          </Button>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="consent-templates">
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:settings.consents.title')}
        </Typography>
        {canWrite && (
          <Button startIcon={<AddIcon />} onClick={() => setPublishing(null)} data-testid="consent-template-add">
            {t('pat:settings.consents.publish')}
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('pat:settings.consents.help')}
      </Typography>
      <DataTable
        captionKey="pat:settings.consents.title"
        columns={columns}
        rows={latest}
        getRowId={(row) => row.id}
        isLoading={templates.isPending}
        error={templates.error}
      />
      <ConsentTemplateDialog type={publishing ?? null} open={publishing !== undefined} onClose={() => setPublishing(undefined)} />
    </Paper>
  );
}
